import { Button, InputField, Label } from "@appretio/ui-kit";
import { useState } from "react";
import {
  DIMENSION_VALUE_PATTERN,
  DIMENSION_VALUE_RULES,
  getFieldLabel
} from "../../forecastContext";
import { useAddDim } from "../../hooks/manageCubes/useAddDim";
import { useContextFilters } from "../../hooks/manageCubes/useContextFilters";
import { useCubeContext } from "../../hooks/manageCubes/useCubeContext";
import { useManageCubesOperationToast } from "../../hooks/manageCubes/useManageCubesOperationToast";
import { ConfirmOperationDialog } from "./ConfirmOperationDialog";
import { CubeContextFilterGrid } from "./CubeContextFilterGrid";
import {
  newDimValueError,
  targetsWholeDimension
} from "./dimensionValueValidation";
import {
  buttonRow,
  errorText,
  fieldGroup,
  formColumn,
  hintText,
  narrowField,
  newValuesSection
} from "./manageCubesOperations.css";
import { useDimensionValueMappings } from "./useDimensionValueMappings";

/** Props for the Add Dimension operation form. */
interface AddOperationProps {
  /** The target cube ID to add a new dimension value to. */
  cubeId: string;
}

export function AddOperation({ cubeId }: AddOperationProps) {
  const [startDate, setStartDate] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const { showSuccess, ToastElement } = useManageCubesOperationToast();
  const { selections, activeFilters, handleFieldChange, resetSelections } =
    useContextFilters();
  const {
    newValues,
    setNewValues,
    errors,
    setErrors,
    mappingDims,
    editedMappings,
    hasValidMapping,
    resetMappings
  } = useDimensionValueMappings(selections);

  const { data: cubeContext, isLoading: contextLoading } =
    useCubeContext(cubeId);

  const { data: filteredContext } = useCubeContext(cubeId, activeFilters);
  const addDim = useAddDim();

  function fieldError(key: string, raw: string): string | undefined {
    const trimmed = raw.trim();
    if (trimmed.length === 0) return undefined;
    if (!DIMENSION_VALUE_PATTERN.test(trimmed)) {
      return `Invalid ${getFieldLabel(key)} name. ${DIMENSION_VALUE_RULES}`;
    }
    if (!targetsWholeDimension(selections, key)) return undefined;
    return newDimValueError(cubeContext?.options?.[key] ?? [], trimmed);
  }

  function setFieldError(key: string, error: string | undefined) {
    setErrors((prev) => {
      const next = { ...prev };
      delete next["_global"];
      if (error) next[key] = error;
      else delete next[key];
      return next;
    });
  }

  function validateNewDimensionValues(): boolean {
    const errs: Record<string, string> = {};

    if (mappingDims.length === 0) {
      errs["_global"] =
        "At least one dimension must have exactly one value selected to serve as a template.";
      setErrors(errs);
      return false;
    }

    if (editedMappings.length === 0) {
      errs["_global"] = "Edit at least one pre-filled value to add a new one.";
      setErrors(errs);
      return false;
    }

    for (const [key, val] of editedMappings) {
      const error = fieldError(key, val);
      if (error) {
        errs[key] = error;
      }
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmit() {
    if (!validateNewDimensionValues()) return;
    setDialogOpen(true);
  }

  function handleConfirm() {
    const context: Record<string, string[]> = {};
    for (const [key, vals] of Object.entries(selections)) {
      if (vals.length > 0) context[key] = vals;
    }

    const nameMapping: Record<string, string> = {};
    for (const [key, val] of editedMappings) {
      nameMapping[key] = val.trim();
    }

    addDim.mutate(
      {
        cube_id: cubeId,
        context,
        name_mapping: nameMapping,
        date_from: startDate || null
      },
      {
        onSuccess: (data) => {
          showSuccess(data.message);
          resetSelections();
          resetMappings();
          setStartDate("");
          setDialogOpen(false);
        },
        // The global mutation handler already toasts the failure with the
        // server's reason — a local toast would duplicate it.
        onError: () => {
          setDialogOpen(false);
        }
      }
    );
  }

  return (
    <>
      {ToastElement}
      <div className={formColumn}>
        <p className={hintText}>
          Select context elements. For target dimension(s), a single value must
          be selected.
        </p>
        <CubeContextFilterGrid
          options={cubeContext?.options}
          filteredOptions={filteredContext?.options}
          selections={selections}
          hasActiveFilters={!!activeFilters}
          isLoading={contextLoading}
          setSelected={handleFieldChange}
        />

        <div className={`${fieldGroup} ${narrowField}`}>
          <Label label="Start from date (optional)" />
          <InputField
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Leave empty for all data"
          />
        </div>

        {mappingDims.length > 0 && (
          <div className={newValuesSection}>
            <p className={hintText}>
              Edit the pre-filled value(s) to add a new one for each target
              dimension.
            </p>
            {mappingDims.map(([key]) => (
              <div key={key} className={`${fieldGroup} ${narrowField}`}>
                <Label label={`New ${getFieldLabel(key)}`} />
                <InputField
                  placeholder={`Enter new ${getFieldLabel(key).toLowerCase()}`}
                  value={newValues[key] ?? ""}
                  onChange={(e) => {
                    setNewValues((prev) => ({
                      ...prev,
                      [key]: e.target.value
                    }));
                    setFieldError(key, fieldError(key, e.target.value));
                  }}
                  error={errors[key]}
                />
              </div>
            ))}
          </div>
        )}

        {errors["_global"] && <p className={errorText}>{errors["_global"]}</p>}

        <div className={buttonRow}>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!hasValidMapping}
            title={
              hasValidMapping
                ? undefined
                : "Edit at least one pre-filled value to add a new one."
            }
          >
            Add
          </Button>
        </div>
      </div>
      <ConfirmOperationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="Add Dimension"
        confirmText="Add"
        onConfirm={handleConfirm}
        isLoading={addDim.isPending}
      >
        <p>
          Add new dimension values using the following context. This operation
          is irreversible.
        </p>
        <p>
          <strong>Context:</strong>
        </p>
        <ul>
          {Object.entries(selections)
            .filter(([, vals]) => vals.length > 0)
            .map(([key, vals]) => (
              <li key={key}>
                <strong>{getFieldLabel(key)}:</strong> {vals.join(", ")}
              </li>
            ))}
        </ul>
        <p>
          <strong>New values:</strong>
        </p>
        <ul>
          {editedMappings.map(([key, val]) => (
            <li key={key}>
              <strong>{getFieldLabel(key)}:</strong> {selections[key][0]} →{" "}
              {val.trim()}
            </li>
          ))}
        </ul>
        {startDate && <p>Starting from: {startDate}</p>}
      </ConfirmOperationDialog>
    </>
  );
}
